import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import Container from '../../../../components/Container';
import Loader from '../../../../components/Loader';
import AlertMsg from '../../../../components/AlertMsg';
import AppDialog from '../../../../components/AppDialog';
import RestaurantHeader from '../../../../components/RestaurantHeader';

import { AllergenList, AllergenFormDialog } from '../components';
import {
  useGetAllergens,
  useCreateAllergen,
  useUpdateAllergen,
  useDeleteAllergen,
} from '../services';
import { TAllergen, TCreateAllergenRequest } from '../schemas';

const AllergensPage = () => {
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const navigate = useNavigate();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedAllergen, setSelectedAllergen] = useState<TAllergen | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [allergenToDelete, setAllergenToDelete] = useState<TAllergen | null>(null);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  const { data: allergens, isLoading, refetch } = useGetAllergens(restaurantId || '');
  const createMutation = useCreateAllergen(restaurantId || '');
  const updateMutation = useUpdateAllergen();
  const deleteMutation = useDeleteAllergen();

  const handleAddClick = () => {
    setSelectedAllergen(null);
    setFormDialogOpen(true);
  };

  const handleEditClick = (allergen: TAllergen) => {
    setSelectedAllergen(allergen);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (allergen: TAllergen) => {
    setAllergenToDelete(allergen);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: TCreateAllergenRequest) => {
    try {
      if (selectedAllergen) {
        await updateMutation.mutateAsync({ id: selectedAllergen._id, payload: data });
        setMsgAlert({ open: true, message: 'Allergen updated successfully', msgType: 'success' });
      } else {
        await createMutation.mutateAsync(data);
        setMsgAlert({ open: true, message: 'Allergen created successfully', msgType: 'success' });
      }
      setFormDialogOpen(false);
      refetch();
    } catch (error) {
      setMsgAlert({
        open: true,
        message: selectedAllergen ? 'Failed to update allergen' : 'Failed to create allergen',
        msgType: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!allergenToDelete) return;

    try {
      await deleteMutation.mutateAsync(allergenToDelete._id);
      setMsgAlert({ open: true, message: 'Allergen deleted successfully', msgType: 'success' });
      setDeleteDialogOpen(false);
      setAllergenToDelete(null);
      refetch();
    } catch (error) {
      setMsgAlert({ open: true, message: 'Failed to delete allergen', msgType: 'error' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAllergenToDelete(null);
  };

  return (
    <Container
      page="restaurants"
      searchVal=""
      onSearch={() => null}
      searchLabel=""
    >
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title="Allergens"
              onBackBtnClick={() => navigate(`/restaurant/${restaurantId}/manage_menu`)}
            />
          </Grid>
          <Grid item xs={12} sx={styles.mainView} px={3} pt={2}>
            <Box sx={styles.headerActions}>
              <Button
                onClick={handleAddClick}
                sx={styles.addBtn}
              >
                + Add Allergen
              </Button>
            </Box>

            <Grid item xs={12} mt={2}>
              <AllergenList
                allergens={allergens || []}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      <AllergenFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        allergen={selectedAllergen}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      <AppDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title={
          <Box>
            <Typography variant="h2" sx={{ fontSize: '1.5rem', color: '#fff' }}>
              Delete Allergen?
            </Typography>
          </Box>
        }
        content={
          <Box>
            Are you sure you want to delete "{allergenToDelete?.name}"? This action cannot be undone.
          </Box>
        }
        firstBtnContent="Cancel"
        secondBtnContent="Delete"
        firstBtnClickHandler={handleDeleteCancel}
        secondBtnClickHandler={handleDeleteConfirm}
        options={{ keepMounted: false, btnLoading: deleteMutation.isPending }}
      />

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default AllergensPage;

const styles = {
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '16px',
  },
  addBtn: {
    backgroundColor: '#000000',
    color: '#ffffff',
    fontSize: 14,
    borderRadius: '8px',
    textTransform: 'capitalize' as const,
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingTop: '8px',
    paddingBottom: '8px',
    '&:hover': {
      backgroundColor: '#333333',
    },
  },
};
